import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	renderInputSelectionToExcel,
	renderMetricsResInSheet,
} from 'containers/phoenix/utils/excelUtil';
import LoadingOverlay from 'react-loading-overlay';
import { ThrobberMessage } from 'components';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react';
import { getMetricsPayload } from 'containers/phoenix/utils/metricsAPIUtil';
import { metricsResFormatToExcelData } from 'containers/phoenix/utils/rowGenerationUtil';
import { generateUniqueIdData, generateDatasetTitle, currentTimeStamp } from 'utils/GetDataUtil';
import { populateTitleToExcel, createHiddenSheet, moveToCellSheet } from 'utils/excelUtils/ExcelDataPopulate';
import { generateTPColumnHeaders, getBenchMarkName, generateTSColumnHeaders, generateTimePeriodColumnHeaders } from 'containers/phoenix/utils/columnGenerationUtil';
import Wizard from './components/Wizard';
import ConfigureInputsStep from './steps/ConfigureInputsStep';
import { PhoenixServices } from './services/PhoenixServices';
import {
	fetchClients,
	setShowLoader,
	fetchAccounts,
	fetchHierarchies,
	fetchBenchmarks,
	fetchMetrics,
} from './redux/PhoenixActions';
import DimensionsConfigurationStep from './steps/DimensionsConfigurationStep';
import MetricsConfigurationStep from './steps/MetricsConfigurationStep';
import DisplayOutputSettingsStep from './steps/DisplayOutputSettingsStep';
import { mergeColumnsAndRender, mergeTPColumnsAndRender } from './utils/excelMergedColumn';
import { convertPhoenixToXDEObject, newDatasetObject } from './utils/datasetUtil';
import { updateDatasetToCosmos } from 'containers/displaySettings/DisplaySettingsAction';

export const PhoenixServiceContainer = ({ history }) => {
	const dispatch = useDispatch();
	const [currentStep, setCurrentStep] = useState(0);
	// const [disableNextBtn, setDisableNextBtn] = useState(false);
	const inputData = useSelector(
		state => state.data.phoenixReducers.inputData,
	);
	const dimensions = useSelector(
		state => state.data.phoenixReducers.dimensions,
	);
	const metrics = useSelector(state => state.data.phoenixReducers.metrics);
	const showLoader = useSelector(
		state => state.data.phoenixReducers.showLoader,
	);
	const selectedServiceData = useSelector(
		state => state.data.createNewDataSet.selectedServiceData,
	);
	const accounts = useSelector(
		state => state.data.phoenixReducers.accounts,
	);
	const profile = useSelector(state => state.data.userInContext.user.profile);
	const displayData = useSelector(
		state => state.data.phoenixReducers.displaySetting,
	);
	const token = useSelector(state => state.data.phoenixReducers.token);
	const existingDataset = useSelector(state => state.data.dataSet.data);
	const selectedDataset = useSelector(state => state.data.phoenixReducers.selectedDataset);
	// const selectedDataset = useSelector(state => state.data.createNewDataSet.selectedDataset);
	const steps = {
		PhoenixTimePeriod: [
			{ title: 'Configure Inputs', content: <ConfigureInputsStep /> },
			{ title: 'Dimensions', content: <DimensionsConfigurationStep /> },
			{ title: 'Metrics', content: <MetricsConfigurationStep /> },
			{ title: 'Display Settings', content: <DisplayOutputSettingsStep /> },
		],
		PhoenixTimeSeries: [
			{ title: 'Configure Inptus', content: <ConfigureInputsStep /> },
			{ title: 'Dimensions', content: <DimensionsConfigurationStep /> },
			{ title: 'Display Settings', content: <DisplayOutputSettingsStep /> },
		],
		PhoenixTimePeriod_AccountsOnly: [
			{ title: 'Configure Inputs', content: <ConfigureInputsStep /> },
			{ title: 'Metrics', content: <MetricsConfigurationStep /> },
			{ title: 'Display Settings', content: <DisplayOutputSettingsStep /> },
		],
		PhoenixTimeSeries_AccountsOnly: [
			{ title: 'Configure Inptus', content: <ConfigureInputsStep /> },
			{ title: 'Display Settings', content: <DisplayOutputSettingsStep /> },
		],
	};

	const getStepsValue = () => {
		let selectedStep = steps[selectedServiceData.serviceId];
		// NOTE: Hierarchy - Accounts Only and for All Levels we dont need to show dimensions step
		if(inputData?.hierarchyLevel?.key === 'Accounts Only' || inputData?.hierarchyLevel?.key === 'All Levels'){
			selectedStep = steps[selectedServiceData.serviceId+'_AccountsOnly'];
		}
		return selectedStep;
	}

	const stepsValue = getStepsValue();

	const fetchBenchmarkOnAccChange = async accountBID => {
		const defaultOption = [
			{
				benchmarkBusinessID: 'None',
				benchmarkUsage: 'None',
			},
		];
		if (inputData?.analysisLevel?.text === 'Custom Group') {
			dispatch(fetchBenchmarks([...defaultOption]));
			return;
		}
		const benchmarkRes = await PhoenixServices.getBenchmarks(
			accountBID,
			token,
		);
		const benchmarkData = benchmarkRes?.data?.data.filter(
			(a, i) =>
				benchmarkRes?.data?.data.findIndex(
					s => a.benchmarkUsage === s.benchmarkUsage,
				) === i,
		);
		dispatch(setShowLoader(false));
		dispatch(fetchBenchmarks([...defaultOption, ...benchmarkData]));
	};

	useEffect(() => {
		if (selectedDataset?.duplicateClicked) {
			dispatch({
				type: 'DUPLICATE_DATASET',
			});
		}
		if (!selectedDataset?.duplicateClicked && inputData?.cellNumber && inputData?.sheetName) {
			moveToCellSheet(inputData?.cellNumber, inputData?.sheetName);
		}
		const loadClients = async () => {
			try {
				dispatch(setShowLoader(true));
				const responseData = await PhoenixServices.getClients(token);
				const clientData = responseData?.data?.data.sort((a, b) => a.ClientName.localeCompare(b.ClientName));
				dispatch(fetchClients(clientData));
				const accountResponse = await PhoenixServices.getAccounts(
					inputData?.client?.SponsorCode ? inputData?.client?.SponsorCode : clientData[0]?.SponsorCode,
					token,
				)
				const accountData = accountResponse?.data?.data?.sort((a, b) => a.accountID.localeCompare(b.accountID));
				const selectedAcc =
					inputData?.accountId[0]?.accountBusinessID ||
					accountData[0]?.accountBusinessID;
				const accountGroupRes = await PhoenixServices.getHierarchies(
					inputData?.client?.SponsorCode ? inputData?.client?.SponsorCode : clientData[0]?.SponsorCode,
					token,
				);
				fetchBenchmarkOnAccChange(selectedAcc);
				dispatch(fetchAccounts(accountResponse?.data?.data));
				dispatch(fetchHierarchies(accountGroupRes?.data?.data));
				dispatch(setShowLoader(false));
				// const responseData = await PhoenixServices.getHierarchies('cb4dd5dbaacc473d4935ac6e55ad3323');
				// dispatch(fetchHierarchies(responseData));
			} catch (err) {
				console.log(err.message);
			}
		};

		loadClients();
	}, [dispatch]);

	const inputCellRange = (name, range) => {
		dispatch({
			type: 'UPDATE_INPUT_RANGE',
			key: 'inputEndRange',
			payload: {
				sheetName: name,
				cellRange: range,
			},
		});
	};

	const createOrUpdateDatasetToCosmos = async dataset => {
		dispatch(updateDatasetToCosmos(JSON.stringify(dataset), false));
	};

	const updateOutputCellRange = (name, range) => {
		const outPutRange = {
			sheetName: name,
			cellRange: range,
		};
		dispatch({
			type: 'UPDATE_OUTPUT_RANGE',
			key: 'outputEndRange',
			payload: outPutRange,
		});

		const { uuId, uniqueId, fullDateFormat } = generateUniqueIdData(
			profile.personaId,
		);
		const reportId = selectedDataset?.editClicked ? selectedDataset?.reportId : uniqueId;
		const report = convertPhoenixToXDEObject(inputData, outPutRange, displayData, dimensions, metrics, selectedServiceData, reportId);

		if (existingDataset?.reports?.length > 0) {
			existingDataset["hiddenWorksheetID"]= uniqueId;
			existingDataset["id"]= uniqueId;
			existingDataset["sheetID"]= uniqueId;
			const reportIndex = existingDataset.reports.findIndex(d => d.reportId === report.reportId);
			if (selectedDataset?.editClicked) {
				report.dataSetOrder = reportIndex;
				existingDataset.reports[reportIndex] = report;
			} else {
				report.dataSetOrder = existingDataset.reports.length + 1;
				existingDataset.reports.push(report);
			}
			createOrUpdateDatasetToCosmos(existingDataset);
		} else {
			const newDataset = newDatasetObject(uniqueId, report);
			createOrUpdateDatasetToCosmos(newDataset);
		}
	};

	const updateCurrentStep = step => {
		if (inputData.cellNumber && inputData.sheetName && step !== -1) {
			if (currentStep === 0) {
				// NOTE: render selected input on next click and update range in reducer to remember next time to clear rendered input from sheet
				renderInputSelectionToExcel(
					inputData,
					selectedServiceData.serviceId,
					inputCellRange,
				);
			}
			setCurrentStep(step);
		} else if (step === -1) {
			history.push(`/create-new-dataset`);
		}
	};

	const renderDatasetTitle = () => {
		const timeStamp = currentTimeStamp();
		dispatch({
			type: 'UPDATE_DISPLAY_SETTINGS',
			value: { id: 'time-stamp', value: timeStamp },
		});
		const datasetTitle = generateDatasetTitle(displayData.displaySetting, profile, timeStamp);
		populateTitleToExcel(datasetTitle, displayData.cellId, displayData.sheetName);
	};

	const getDisplayError = displayData => {
		const displayDetails = displayData.displaySetting.filter(d => d.id === 'dataset-title');
		if(displayDetails.length > 0  && displayDetails[0].error) {
			return true;
		}
		return false;
	}

	const getMetricsData = async () => {
		renderDatasetTitle();
		dispatch(setShowLoader(true));
		// const { uuId, uniqueId, fullDateFormat } = generateUniqueIdData(
		// 	profile.personaId,
		// );
		const payload = getMetricsPayload(inputData, selectedServiceData.serviceId, dimensions, metrics);
		const responseData = await PhoenixServices.getMetrics(payload, token);
		const accountsMap = {};
		accounts.map(acc => accountsMap[acc.accountBusinessID] = acc);
		if (selectedServiceData.serviceId === 'PhoenixTimeSeries') {
				const {columns, mappings} = generateTSColumnHeaders(responseData.data.response, inputData, accountsMap)
				mergeColumnsAndRender(responseData.data.response,inputData,columns,mappings,accountsMap,updateOutputCellRange);
		} else {
			let benchMarkName = '';
			if (inputData ?.benchmark ?.benchmarkUsage !== "None") {
				benchMarkName = getBenchMarkName(responseData.data.response);
			}
			const {tpColumns, tpMappings, headers} = generateTimePeriodColumnHeaders(responseData.data.response, metrics, inputData, accountsMap, benchMarkName)

			mergeTPColumnsAndRender(responseData.data.response,inputData,tpColumns,tpMappings,headers,accountsMap,updateOutputCellRange);

			// const { headers, headersWithCode } = generateTPColumnHeaders(metrics, inputData, benchMarkName);
			// const result = metricsResFormatToExcelData(inputData, accountsMap, responseData.data.response, headers, headersWithCode);
			// renderMetricsResInSheet(inputData, result, updateOutputCellRange);
		}
		// dispatch(fetchMetrics(responseData));
		dispatch(setShowLoader(false));

	};

	return (
		<LoadingOverlay
			active={showLoader}
			spinner={<Spinner size={SpinnerSize.large} />}
			text={<ThrobberMessage />}
		>
			<Wizard
				steps={stepsValue}
				currentStep={currentStep}
				setCurrentStep={updateCurrentStep}
				history={history}
				isError={getDisplayError(displayData)}
				getData={getMetricsData}
			/>
		</LoadingOverlay>
	);
};
